import React, { useState, useEffect } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites } from 'store/sitesSlice';
import { RootState } from 'store/index';
import { commonGridStyles } from 'styles/gridStyles';
import styles from './Rakeback.module.scss';
import ComponentLoader from 'components/common/ComponentLoader';
import { formatDate } from 'utils/date';
import commonStyles from 'styles/common.module.scss';

interface RakebackItem {
  id: string;
  user: string;
  userId: string;
  site: string;
  amount: number;
  date: string;
}

const MOCK_RAKEBACKS: RakebackItem[] = [
  {
    id: '38',
    user: 'artak',
    userId: '123',
    site: 'dgenta',
    amount: 15.2,
    date: new Date().toISOString(),
  },
  {
    id: '2',
    user: 'player1',
    userId: '456',
    site: 'Demo Casino',
    amount: 320.5,
    date: new Date(Date.now() - 7200000).toISOString(),
  },
  {
    id: '3',
    user: 'agent1',
    userId: '789',
    site: 'dgenta',
    amount: 50.0,
    date: new Date(Date.now() - 14400000).toISOString(),
  },
  {
    id: '4',
    user: 'Levon',
    userId: '101',
    site: 'dgenta',
    amount: 8.25,
    date: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: '5',
    user: 'nano2026',
    userId: '202',
    site: 'dgenta',
    amount: 120.0,
    date: new Date(Date.now() - 172800000).toISOString(),
  },
];

const Rakeback: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );

  const [selectedSite, setSelectedSite] = useState<string | number>('all');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    }
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (sites.length > 0 && isInitialLoad) {
      const dgentaSite = sites.find((s) => s.title.toLowerCase().includes('dgenta'));
      if (dgentaSite) {
        setSelectedSite(dgentaSite.id);
        setIsInitialLoad(false);
      }
    }
  }, [sites, isInitialLoad]);

  const filteredRows = React.useMemo(() => {
    return MOCK_RAKEBACKS.filter((row) => {
      let matchesSite = true;
      if (selectedSite !== 'all') {
        const site = sites.find((s) => s.id === selectedSite);
        matchesSite = site ? row.site.toLowerCase() === site.title.toLowerCase() : true;
      }
      return matchesSite;
    });
  }, [selectedSite, sites]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'user',
      headerName: 'User',
      width: 200,
      renderCell: (params) => (
        <span
          className={styles.userLink}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${params.row.id}`);
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      renderCell: (params) => (
        <Typography className={styles.amount}> $ {params.row.amount.toLocaleString()} </Typography>
      ),
    },
    { field: 'site', headerName: 'Site', width: 150 },
    {
      field: 'date',
      headerName: 'Date',
      width: 250,
      valueFormatter: (value) => formatDate(value),
    },
  ];

  return (
    <Box className={styles.rakebackContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Rakeback History
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={selectedSite}
                label="Site"
                onChange={(e) => setSelectedSite(e.target.value as string | number)}
                disabled={sitesLoading}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="all">All Sites</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={sitesLoading}
          sx={{ ...commonGridStyles, mt: 6 }}
        />
      </Box>
    </Box>
  );
};

export default Rakeback;
